// Component to display agent activity animations (thinking, tool calling, search)
import React from 'react';
import { Brain, Wrench, Search, Loader2, CheckCircle, XCircle } from 'lucide-react';
import { AgentActivity } from '@/types/chat';
import { useTranslation } from 'react-i18next';
import { normalizeToolName } from '@/lib/agent/toolNameUtils';
import { isInternalAgentActivity } from '@/lib/agent/activityFilters';

interface AgentActivityIndicatorProps {
  activities: AgentActivity[];
  className?: string;
}

const ActivityIcon: React.FC<{ activity: AgentActivity }> = ({ activity }) => {
  const iconClass = "h-4 w-4";

  // Agent card should remain a brain icon regardless of status transitions.
  if (activity.type === 'thinking') {
    return <Brain className={`${iconClass} text-purple-500 animate-pulse`} />;
  }

  if (activity.status === 'success') {
    return <CheckCircle className={`${iconClass} text-green-500`} />;
  }
  
  if (activity.status === 'error') {
    return <XCircle className={`${iconClass} text-red-500`} />;
  }
  
  // Running or start status
  switch (activity.type) {
    case 'tool_call':
      return <Wrench className={`${iconClass} text-blue-500 animate-pulse`} />;
    case 'search':
      return <Search className={`${iconClass} text-amber-500 animate-pulse`} />;
    case 'node_execution':
      return <Loader2 className={`${iconClass} text-neutral-500 animate-spin`} />;
    default:
      return <Loader2 className={`${iconClass} text-neutral-500 animate-spin`} />;
  }
};

const cleanLabel = (label: string): string =>
  label.replace(/\s*supabase\s*/gi, ' ').trim();

const normalizeThought = (value: unknown): string | undefined => {
  if (value == null) return undefined;
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

// Map tool labels to translation keys
const TOOL_LABEL_MAP: Record<string, string> = {
  'get_schema_and_table': 'agent_tools.get_schema_and_table',
  'lead_gen_v9': 'agent_tools.lead_gen_v9',
  'google_instagram_search': 'agent_tools.google_instagram_search',
  'google_ig_tt_yt_search': 'agent_tools.google_ig_tt_yt_search',
  'create_post': 'agent_tools.create_post',
  'query_supabase_v2': 'agent_tools.query_supabase_v2',
  'query_database': 'agent_tools.query_database',
  'scrape_tiktok_profile': 'agent_tools.scrape_tiktok_profile',
  'scrape_instagram_profile': 'agent_tools.scrape_instagram_profile',
  'scrape_youtube_profile': 'agent_tools.scrape_youtube_profile',
  'link_captioner': 'agent_tools.link_captioner',
  'cobalt_link': 'agent_tools.cobalt_link',
  'fetch_youtube_video_detail': 'agent_tools.fetch_youtube_video_detail',
  'fetching_audience': 'agent_tools.fetching_audience',
  'fetch_audience': 'agent_tools.fetching_audience', // alias for fetching_audience
  'searching_web': 'agent_tools.searching_web',
  'creating_post': 'agent_tools.creating_post',
  'deep_statistics_agent': 'agent_tools.deep_statistics_agent',
};

const translateToolLabel = (label: string, t: (key: string) => string): string => {
  const { normalized, versionNormalized, versionStripped } = normalizeToolName(label);

  // Check both versioned and non-versioned forms
  const key = TOOL_LABEL_MAP[versionNormalized] || TOOL_LABEL_MAP[versionStripped] || TOOL_LABEL_MAP[normalized];
  return key ? t(key) : label;
};

const ActivityLabel: React.FC<{ activity: AgentActivity }> = ({ activity }) => {
  const { t } = useTranslation();
  const tr = React.useCallback(
    (key: string, fallback: string) => {
      const translated = t(key);
      return translated === key ? fallback : translated;
    },
    [t],
  );
  const agentTitle = tr('agent_thinking.agent', 'Agent');

  const getLabel = () => {
    switch (activity.type) {
      case 'thinking':
        return agentTitle;
      case 'tool_call':
        const toolLabel = (activity.data?.tool_name as string | undefined) || activity.label || t('agent_tools.tool_call');
        return cleanLabel(translateToolLabel(toolLabel, t));
      case 'search':
        return t('agent_tools.searching_web');
      case 'node_execution':
        return agentTitle;
      default:
        return agentTitle;
    }
  };

  return (
    <span className="text-xs text-neutral-600 dark:text-neutral-400">
      {getLabel()}
    </span>
  );
};

const ADVANCE_INTERVAL_MS = 3000;

export const PendingToolCard: React.FC<{ className?: string }> = ({ className = '' }) => {
  const { t } = useTranslation();
  const tr = React.useCallback(
    (key: string, fallback: string) => {
      const translated = t(key);
      return translated === key ? fallback : translated;
    },
    [t],
  );
  const [stepIndex, setStepIndex] = React.useState(0);
  const pendingSteps = React.useMemo(
    () => [
      tr('agent_thinking.analyzing_user_question', 'Analyzing User Question'),
      tr('agent_thinking.applying_socmed_best_practices', 'Applying Socmed Best Practices'),
      tr('agent_thinking.researching_platform', 'Researching the Platform'),
      tr('agent_thinking.diving_to_data', 'Diving to Data'),
      tr('agent_thinking.planning', 'Planning...'),
    ],
    [tr],
  );

  React.useEffect(() => {
    if (stepIndex >= pendingSteps.length - 1) return;
    const timer = setTimeout(() => {
      setStepIndex(prev => Math.min(prev + 1, pendingSteps.length - 1));
    }, ADVANCE_INTERVAL_MS);
    return () => clearTimeout(timer);
  }, [pendingSteps.length, stepIndex]);

  React.useEffect(() => {
    setStepIndex(0);
  }, []);

  return (
    <div className={`flex items-center space-x-2 px-3 py-2 rounded-lg bg-neutral-100 dark:bg-neutral-800 ${className}`}>
      <Loader2 className="h-4 w-4 text-neutral-500 animate-spin" />
      <span className="text-xs text-neutral-600 dark:text-neutral-400">
        {pendingSteps[stepIndex]}
      </span>
    </div>
  );
};

export const AgentActivityIndicator: React.FC<AgentActivityIndicatorProps & { showPendingCard?: boolean }> = ({
  activities,
  className = '',
  showPendingCard = false,
}) => {
  const visibleActivities = React.useMemo(() => {
    if (!activities) return [];
    return activities.filter(a => {
      if (isInternalAgentActivity(a)) return false;
      const nodeData = a.data?.raw?.data || a.data;
      const nt = typeof nodeData?.node_type === 'string' ? nodeData.node_type.toLowerCase() : '';
      if (nt === 'start') return false;
      if (!a.label) {
        if (process.env.NODE_ENV === 'development') {
          console.log('[AgentActivityIndicator] Filtering out unlabeled activity:', {
            type: a.type,
            status: a.status,
            label: a.label,
            data: a.data,
          });
        }
        return false;
      }
      return true;
    });
  }, [activities]);

  if (visibleActivities.length === 0) return null;

  const displayedActivity = visibleActivities[visibleActivities.length - 1];

  return (
    <div className={`space-y-2 ${className}`}>
      <div className="flex items-center space-x-2 px-3 py-2 rounded-lg bg-neutral-100 dark:bg-neutral-800">
        <ActivityIcon activity={displayedActivity} />
        <ActivityLabel activity={displayedActivity} />
        {displayedActivity.type === 'thinking' && displayedActivity.data?.thought && (
          <div className="ml-2 text-xs text-neutral-500 dark:text-neutral-400 italic max-w-md truncate">
            {normalizeThought(displayedActivity.data.thought)}
          </div>
        )}
      </div>
      {showPendingCard && <PendingToolCard />}
    </div>
  );
};

// Component to show detailed activity log (expandable)
export const AgentActivityLog: React.FC<{ activities: AgentActivity[] }> = ({ activities }) => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  
  if (!activities || activities.length === 0) return null;
  const filtered = activities
    .filter(a => !isInternalAgentActivity(a))
    .filter(a => a.type === 'thinking' || a.type === 'tool_call');
  if (filtered.length === 0) return null;
  
  return (
    <div className="mt-2 border-t border-neutral-200 dark:border-neutral-700 pt-2">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="text-xs text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300 flex items-center space-x-1"
      >
        <span>{isExpanded ? '▼' : '▶'}</span>
        <span>Activity Log ({filtered.length})</span>
      </button>
      
      {isExpanded && (
        <div className="mt-2 space-y-1 max-h-48 overflow-y-auto">
          {filtered.map((activity, index) => (
            <div
              key={index}
              className="flex items-start space-x-2 text-xs p-2 rounded bg-neutral-50 dark:bg-neutral-800"
            >
              <ActivityIcon activity={activity} />
              <div className="flex-1 min-w-0">
                <ActivityLabel activity={activity} />
                {activity.data?.thought && (
                  <div className="text-neutral-500 dark:text-neutral-400 mt-1">
                    {normalizeThought(activity.data.thought)}
                  </div>
                )}
                {activity.data?.action && (
                  <div className="text-neutral-500 dark:text-neutral-400 mt-1">
                    Action: {normalizeThought(activity.data.action)}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
